"use client";

import {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
  startTransition,
  Suspense,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from "react";
import dynamic from "next/dynamic";
import { useRouter } from "next/navigation";
import type { KundaliResponse } from "@/lib/api/kundali";
import { useDeliveryCapture } from "./KundaliFormCard.delivery";
import { makeHandleSubmit } from "./KundaliFormCard.submit";
import type { PlaceResult } from "@/lib/api/misc";
// Kundali-form analytics imports go straight to the source module instead of
// re-routing through `@/lib/analytics`. The barrel re-export there
// (`export * from "@/lib/analytics-kundali"`) created a chunk-resolution race
// in production builds — `(0, o.trackKundaliFormStarted) is not a function`
// (Sentry 7481455768) fired when the consumer chunk loaded before the kundali
// chunk hydrated the namespace object. Direct imports avoid the indirection.
import { trackKundaliFormStarted } from "@/lib/analytics-kundali";
import { DailyInsightSlot } from "./KundaliFormCard.controls";
import { KundaliInputForm, type CapturedSEOEntry } from "./KundaliFormCard.parts";
import { getKundaliTranslations, type KundaliLang } from "./KundaliFormCard.i18n";
import { LOADING_STEPS, STEP_DELAY_MS } from "./KundaliFormCard.loading";
import { useCapturedSEOEntry } from "./KundaliFormCard.seo";
import type { KundaliFieldErrors } from "./KundaliFormCard.validation";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import DailyPanchangLink from "@/components/DailyPanchangLink";
import KundaliSavedLink from "./KundaliSavedLink";

const SpiritualReveal = dynamic(() => import("@ishvaram/ui/SpiritualReveal"), { ssr: false });
const KundaliResult = dynamic(() => import("@/components/KundaliResult"), { ssr: false });

// Warm the result chunk the moment the seeker starts the form, so by the time
// they submit, the `dynamic(ssr:false)` chunk is already in the module cache and
// the result swaps in within a frame — no `Suspense` gap (CLS). `dynamic()` and
// this raw `import()` resolve the SAME webpack module, so one warms the other.
function preloadKundaliResult() {
  void import("@/components/KundaliResult");
}

type KundaliFormCardProps = {
  defaultLanguage?: KundaliLang;
  source?: string;
  submitLabel?: string;
  savedNote?: string;
  showDailyInsightCard?: boolean;
  containerClassName?: string;
  namePlaceholder?: string;
  placePlaceholder?: string;
  requireBirthTime?: boolean;
  reassuranceNote?: string;
  reassuranceNoteHi?: string;
  loadingLabel?: string;
  loadingLabelHi?: string;
  seoEntryOverride?: CapturedSEOEntry | null;
};

export default function KundaliFormCard({
  defaultLanguage = "en",
  source = "home",
  submitLabel,
  savedNote,
  showDailyInsightCard = true,
  containerClassName = "mx-auto max-w-md px-4 sm:px-6 pb-16",
  namePlaceholder,
  placePlaceholder,
  requireBirthTime = false,
  reassuranceNote,
  reassuranceNoteHi,
  loadingLabel,
  loadingLabelHi,
  seoEntryOverride,
}: KundaliFormCardProps) {
  const router = useRouter();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<KundaliFieldErrors>({});
  const [result, setResult] = useState<KundaliResponse | null>(null);
  const [language, setLanguage] = useState<KundaliLang>(defaultLanguage);
  const [activeField, setActiveField] = useState<string | null>(null);
  const [formStarted, setFormStarted] = useState(false);
  const loadingStep = useLoadingStep(loading);
  const showDailyInsight = useDeferredDailyInsight();
  const capturedSeoEntry = useCapturedSEOEntry(seoEntryOverride);
  const seoEntry = seoEntryOverride !== undefined ? seoEntryOverride : capturedSeoEntry;
  const [name, setName] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [birthTime, setBirthTime] = useState("");
  const [birthPlace, setBirthPlace] = useState("");
  const [selectedPlace, setSelectedPlace] = useState<PlaceResult | null>(null);
  const delivery = useDeliveryCapture(language); // form-level delivery capture (ADR-0026)
  const updateField = makeFieldUpdater(setError, setFieldErrors);
  const markFormStarted = useMarkFormStarted({
    setActiveField,
    formStarted,
    setFormStarted,
    language,
    seoEntry,
    source,
  });

  const handleSubmit = makeHandleSubmit({
    seoEntry,
    source,
    requireBirthTime,
    name,
    birthDate,
    birthTime,
    birthPlace,
    language,
    selectedPlace,
    delivery,
    router,
    setLoading,
    setError,
    setFieldErrors,
    setResult,
  });

  // Warm the result chunk on first form interaction (high intent, well before
  // submit) so the swap never waits on a network round-trip for the chunk.
  useEffect(() => {
    if (formStarted) preloadKundaliResult();
  }, [formStarted]);

  if (result) return renderKundaliResult(result, language, source, () => setResult(null));

  const t = getKundaliTranslations(language, {
    submitLabel,
    savedNote,
    loadingLabel: localizedOverride(language, loadingLabel, loadingLabelHi),
    namePlaceholder,
    placePlaceholder,
  });
  const localizedReassuranceNote = localizedOverride(language, reassuranceNote, reassuranceNoteHi);

  return (
    <>
      <KundaliSpiritualReveal loading={loading} language={language} loadingStep={loadingStep} />
      <KundaliInputForm
        containerClassName={containerClassName}
        onSubmit={handleSubmit}
        seoEntry={seoEntry}
        language={language}
        setLanguage={setLanguage}
        name={name}
        setName={(value) => updateField("name", value, setName)}
        birthDate={birthDate}
        setBirthDate={(value) => updateField("birthDate", value, setBirthDate)}
        birthTime={birthTime}
        setBirthTime={setBirthTime}
        requireBirthTime={requireBirthTime}
        birthPlace={birthPlace}
        setBirthPlace={(value) => updateField("birthPlace", value, setBirthPlace)}
        setSelectedPlace={setSelectedPlace}
        activeField={activeField}
        setActiveField={setActiveField}
        markFormStarted={markFormStarted}
        fieldErrors={fieldErrors}
        error={error}
        loading={loading}
        loadingStep={loadingStep}
        reassuranceNote={localizedReassuranceNote}
        deliveryField={delivery.field}
        t={t}
      />
      <DailyInsightSlot show={showDailyInsightCard} ready={showDailyInsight} language={language} />
    </>
  );
}

function localizedOverride(language: KundaliLang, en?: string, hi?: string): string | undefined {
  return language === "hi" ? (hi ?? en) : en;
}

function useMarkFormStarted(args: {
  setActiveField: (field: string) => void;
  formStarted: boolean;
  setFormStarted: (value: boolean) => void;
  language: KundaliLang;
  seoEntry: CapturedSEOEntry | null;
  source: string;
}) {
  const { setActiveField, formStarted, setFormStarted, language, seoEntry, source } = args;
  return useCallback(
    (firstField: string) => {
      setActiveField(firstField);
      if (formStarted) return;
      setFormStarted(true);
      trackKundaliFormStarted({
        language,
        first_field: firstField,
        source: seoEntry ? `seo:${seoEntry.config.intent}` : source,
        source_path:
          seoEntry?.sourcePath ||
          (typeof window !== "undefined" ? window.location.pathname || undefined : undefined),
      });
    },
    [setActiveField, formStarted, setFormStarted, language, seoEntry, source]
  );
}

function makeFieldUpdater(
  setError: Dispatch<SetStateAction<string | null>>,
  setFieldErrors: Dispatch<SetStateAction<KundaliFieldErrors>>
) {
  return function updateField<K extends keyof KundaliFieldErrors>(
    field: K,
    value: string,
    setter: (next: string) => void
  ) {
    setter(value);
    setError(null);
    setFieldErrors((current) => {
      if (!current[field]) return current;
      const next = { ...current };
      delete next[field];
      return next;
    });
  };
}

function renderKundaliResult(
  result: KundaliResponse,
  language: KundaliLang,
  source: string,
  onBack: () => void
) {
  const layout = source === "ai_astrologer" ? "chat-first" : "result-first";
  const postResultAdjuncts =
    layout === "result-first" && result.id ? (
      <KundaliPostResultAdjuncts result={result} language={language} />
    ) : null;
  const content = (
    <KundaliResultBoundary
      result={result}
      language={language}
      onBack={onBack}
      layout={layout}
      postResultAdjuncts={postResultAdjuncts}
    />
  );
  if (layout !== "chat-first") {
    return content;
  }
  return <div className="ai-chat-result-active">{content}</div>;
}

function KundaliPostResultAdjuncts({
  result,
  language,
}: {
  result: KundaliResponse;
  language: KundaliLang;
}) {
  // Result-first entries keep the inline result AND get its durable return
  // path: every generated kundali is persisted under result.id, and
  // /kundali/<id> re-renders the same KundaliResult tree (rule 46 — the
  // inline value moment stays put; the saved link is additive). Keep these
  // adjuncts inside KundaliResult so the dynamic result chunk and cards commit
  // together instead of the cards rendering first and shifting downward.
  return (
    <>
      <KundaliSavedLink readingId={result.id} language={language} />
      <DailyPanchangLink
        readingId={result.id}
        language={language === "hi" ? "hi" : "en"}
        source="kundali_result"
      />
    </>
  );
}

function KundaliResultBoundary({
  result,
  language,
  onBack,
  layout,
  postResultAdjuncts,
}: {
  result: KundaliResponse;
  language: KundaliLang;
  onBack: () => void;
  layout: "result-first" | "chat-first";
  postResultAdjuncts: ReactNode;
}) {
  return (
    <ErrorBoundary>
      {/* Suspense boundary is required: KundaliResult is a dynamic(ssr:false)
          client component that calls useSearchParams(); without a boundary the
          searchParams read deopts the render and the chunk-load placeholder vs
          loaded component differ in hook count → "Rendered more hooks than
          during the previous render" crashes on /ai-astrologer + /free-kundali. */}
      <Suspense fallback={<KundaliResultLoadingSkeleton />}>
        <KundaliResult
          result={result}
          language={language}
          onBack={onBack}
          layout={layout}
          postResultAdjuncts={postResultAdjuncts}
        />
      </Suspense>
    </ErrorBoundary>
  );
}

// Reserved-space placeholder shown for the (usually sub-frame, thanks to the
// preload) window between the form unmounting and the result chunk committing.
// Two jobs, both load-bearing for CLS:
//   1. Reserve a full viewport of height so the swap never collapses to 0px —
//      the `fallback={null}` gap is what let the result pop 0px->full-height and
//      shove the always-present SEO content below it (CrUX p75 CLS ≈ 1.0 mobile
//      on /free-kundali/, even though on-load CLS is clean).
//   2. Anchor this swap container to the top of the viewport BEFORE paint, so the
//      form->skeleton->result growth happens below the fold and never enters the
//      viewport's layout-shift accounting. KundaliResult re-applies its own
//      scroll once committed; that second move is a scroll, not a layout shift.
function KundaliResultLoadingSkeleton() {
  const ref = useRef<HTMLDivElement | null>(null);
  useLayoutEffect(() => {
    if (typeof window === "undefined") return;
    ref.current?.scrollIntoView({ block: "start", behavior: "auto" });
  }, []);
  return <div ref={ref} aria-hidden className="mx-auto max-w-3xl min-h-screen px-4 py-8" />;
}

function useDeferredDailyInsight() {
  const [showDailyInsight, setShowDailyInsight] = useState(false);
  useEffect(() => {
    const load = () => startTransition(() => setShowDailyInsight(true));
    const idle = globalThis.requestIdleCallback;
    const id =
      typeof idle === "function"
        ? idle(load, { timeout: 5000 })
        : globalThis.setTimeout(load, 5000);
    return () => cancelDeferredDailyInsight(id);
  }, []);
  return showDailyInsight;
}

function cancelDeferredDailyInsight(id: number) {
  if (typeof globalThis.cancelIdleCallback === "function") {
    globalThis.cancelIdleCallback(id);
  } else {
    globalThis.clearTimeout(id);
  }
}

function useLoadingStep(loading: boolean) {
  const [loadingStep, setLoadingStep] = useState(0);
  useEffect(() => {
    startTransition(() => setLoadingStep(0));
    if (!loading) return;
    const timers = loadingTimers(setLoadingStep);
    return () => timers.forEach(clearTimeout);
  }, [loading]);
  return loadingStep;
}

function loadingTimers(setLoadingStep: (step: number) => void) {
  const timers: ReturnType<typeof setTimeout>[] = [];
  for (let i = 1; i < LOADING_STEPS.length; i++) {
    timers.push(setTimeout(() => startTransition(() => setLoadingStep(i)), STEP_DELAY_MS * i));
  }
  return timers;
}

function KundaliSpiritualReveal({
  loading,
  language,
  loadingStep,
}: {
  loading: boolean;
  language: KundaliLang;
  loadingStep: number;
}) {
  if (!loading) return null;
  return (
    <SpiritualReveal loading language={language} steps={LOADING_STEPS} currentStep={loadingStep} />
  );
}
